import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Wordmark } from "../components/Wordmark";
import { useMesh, VideoTile } from "../lib/useMesh";
import { brandVars } from "../lib/brand";
import { Loader2, AlertCircle, CheckCircle2, Video, Building2, Mic, MicOff, Camera, CameraOff, LogOut, Users } from "lucide-react";

const pub = axios.create({ baseURL: API });

export default function GroupRoomJoin() {
  const { code } = useParams();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [joined, setJoined] = useState(false);
  const [ended, setEnded] = useState(false);
  const [removed, setRemoved] = useState(false);
  const [me, setMe] = useState(() => { try { return JSON.parse(sessionStorage.getItem(`g:${code}`)) || null; } catch { return null; } });
  const [entry, setEntry] = useState({ name: "", role: "Client", dob: "" });
  const { localStream, peers, camError, lastMessage, muted, camOff, toggleMute, toggleCam } = useMesh({ code, pid: me?.id, token: me?.token, enabled: joined && !ended && !removed });

  const load = () => pub.get(`/public/group/${code}`, { params: me?.token ? { t: me.token } : {} }).then((r) => { setInfo(r.data); if (r.data.status === "ended") setEnded(true); })
    .catch((e) => setErr(e?.response?.data?.detail || "This group session isn't available."));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [code, me?.token]);
  useEffect(() => { if (me && !joined && !ended) { const t = setInterval(load, 5000); return () => clearInterval(t); } /* eslint-disable-next-line */ }, [me, joined, ended]);
  useEffect(() => {
    if (!lastMessage) return;
    if (lastMessage.type === "room_status" && lastMessage.status === "ended") setEnded(true);
    if (lastMessage.type === "removed") setRemoved(true);
  }, [lastMessage]);

  const enter = async () => {
    setBusy(true); setErr("");
    try { const { data } = await pub.post(`/public/group/${code}/enter`, entry); const m = { id: data.id, token: data.token, name: entry.name }; sessionStorage.setItem(`g:${code}`, JSON.stringify(m)); setMe(m); }
    catch (e) { setErr(e?.response?.data?.detail || "Could not enter the lobby."); }
    setBusy(false);
  };
  const leave = async () => { try { await pub.post(`/public/group/${code}/leave`, null, { params: { t: me.token } }); } catch {} sessionStorage.removeItem(`g:${code}`); setMe(null); setJoined(false); };

  if (err && !info) return <Center><Card><AlertCircle className="mx-auto mb-3 h-10 w-10 text-amber-500" /><p className="font-semibold">{err}</p></Card></Center>;
  if (!info) return <Center><Loader2 className="h-8 w-8 animate-spin text-primary" /></Center>;
  const color = info.company.primary_color || "#0B3B3C";
  const admitted = info.me?.status === "admitted";
  const peerList = Object.entries(peers);

  return (
    <div className="min-h-screen text-white" style={{ background: color, ...brandVars(color) }}>
      <div className={`mx-auto flex min-h-screen flex-col px-4 py-5 ${joined ? "max-w-6xl" : "max-w-lg"}`}>
        <div className="mb-4 flex items-center gap-3">
          {info.company.logo ? <img src={info.company.logo} alt="" className="h-10 w-10 rounded-xl bg-white object-contain p-0.5" /> : <Building2 className="h-8 w-8" />}
          <div><p className="font-head text-lg font-extrabold leading-tight">{info.company.display_name}</p><p className="text-xs text-white/70">{info.title} · hosted by {info.host_name}</p></div>
        </div>

        {ended || removed ? (
          <Card><CheckCircle2 className="mx-auto mb-2 h-14 w-14 text-emerald-600" /><p className="font-head text-xl font-extrabold">{removed ? "You were removed from the session" : "Session ended"}</p><p className="text-sm text-muted-foreground">Thank you. You may close this page.</p></Card>
        ) : !me ? (
          <Card>
            <p className="text-left text-2xl font-extrabold tracking-tight">Join the group session</p>
            <p className="mt-1 text-left text-sm text-muted-foreground">Tell {info.host_name} who you are. Only the host sees this before you're admitted.</p>
            <div className="mt-4 grid gap-3 text-left">
              <div className="space-y-1"><Label>Your name</Label><Input value={entry.name} onChange={(e) => setEntry({ ...entry, name: e.target.value })} className="h-12" data-testid="group-enter-name" /></div>
              <div className="space-y-1"><Label>I am the…</Label>
                <select className="h-12 w-full rounded-xl border border-border bg-white px-3 text-sm" value={entry.role} onChange={(e) => setEntry({ ...entry, role: e.target.value })} data-testid="group-enter-role">{info.roles.map((r) => <option key={r}>{r}</option>)}</select></div>
              {entry.role === "Client" && <div className="space-y-1"><Label>Date of birth</Label><Input type="date" value={entry.dob} onChange={(e) => setEntry({ ...entry, dob: e.target.value })} className="h-12" data-testid="group-enter-dob" /></div>}
            </div>
            {err && <p className="mt-2 text-sm text-destructive" role="alert">{err}</p>}
            <Button className="mt-4 h-14 w-full rounded-full text-base" disabled={busy || entry.name.trim().length < 2} onClick={enter} data-testid="group-enter">Enter lobby</Button>
          </Card>
        ) : !admitted ? (
          <Card data-testid="group-lobby">
            <Loader2 className="mx-auto mb-3 h-10 w-10 animate-spin text-primary" />
            <p className="text-xl font-extrabold">You're in the lobby</p>
            <p className="mt-1 text-sm text-muted-foreground">{info.host_name} will admit you shortly. Keep this page open — nobody else can see you yet.</p>
            <button className="mt-4 text-xs text-muted-foreground underline" onClick={leave} data-testid="group-leave-lobby">Leave lobby</button>
          </Card>
        ) : !joined ? (
          <Card>
            <p className="font-head text-2xl font-extrabold tracking-tight">Hi {me.name.split(" ")[0]},</p>
            <p className="mt-2 text-sm text-muted-foreground">{info.host_name} has admitted you. Joining turns on your camera and microphone.</p>
            <Button className="mt-5 h-14 w-full rounded-full text-base" onClick={() => setJoined(true)} data-testid="group-join"><Video className="mr-2 h-5 w-5" /> Join group video</Button>
          </Card>
        ) : (
          <div className="flex flex-1 flex-col gap-3">
            <div className="flex items-center justify-between text-xs text-white/70"><span className="flex items-center gap-1" data-testid="group-peer-count"><Users className="h-4 w-4" /> {peerList.length + 1} in session</span>{camError && <span className="text-rose-200">{camError}</span>}</div>
            <div className={`grid flex-1 gap-2 ${peerList.length >= 8 ? "grid-cols-3 md:grid-cols-5" : peerList.length >= 3 ? "grid-cols-2 md:grid-cols-3" : "grid-cols-1 md:grid-cols-2"}`} data-testid="group-grid">
              <VideoTile stream={localStream} label={`${me.name} (you)`} muted mirror />
              {peerList.map(([id, p]) => <VideoTile key={id} stream={p.stream} label={p.name || "Participant"} sub={p.role} />)}
            </div>
            <div className="flex justify-center gap-2">
              <Button variant="secondary" className="rounded-full" onClick={() => toggleMute()} data-testid="group-toggle-mute">{muted ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}</Button>
              <Button variant="secondary" className="rounded-full" onClick={toggleCam} data-testid="group-toggle-cam">{camOff ? <CameraOff className="h-4 w-4" /> : <Camera className="h-4 w-4" />}</Button>
              <Button variant="destructive" className="rounded-full" onClick={leave} data-testid="group-leave"><LogOut className="mr-1 h-4 w-4" /> Leave</Button>
            </div>
          </div>
        )}
        <p className="mt-4 text-center text-xs text-white/60">Powered by <Wordmark light className="text-xs" /></p>
      </div>
    </div>
  );
}

const Center = ({ children }) => <div className="flex min-h-screen items-center justify-center bg-background p-4">{children}</div>;
const Card = ({ children, ...rest }) => <div className="rounded-[2rem] border border-border bg-card p-6 text-center text-foreground" {...rest}>{children}</div>;
